import axios from 'axios';
import {
  SleeperLeague,
  SleeperRoster,
  SleeperMatchup,
  SleeperUser,
  ApiResponse,
  League,
  Roster,
  Matchup,
  User,
  Player
} from '../types/fantasy';

const BASE_URL = 'https://api.sleeper.app/v1';

export class SleeperAPI {
  private playerCache: { [key: string]: any } = {};

  private async makeRequest<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      // Check if we're running on Vercel and need to use proxy
      const isProduction = typeof window !== 'undefined' && window.location.hostname !== 'localhost';
      let url: string;
      
      if (isProduction) {
        // Use our proxy endpoints for Vercel deployment
        if (endpoint.startsWith('/league/')) {
          // Convert Sleeper API paths to our proxy paths
          const path = endpoint.substring(1); // Remove leading slash
          if (path.includes('/rosters')) {
            const leagueId = path.split('/')[1];
            url = `/api/sleeper/rosters/${leagueId}`;
          } else if (path.includes('/users')) {
            const leagueId = path.split('/')[1];
            url = `/api/sleeper/users/${leagueId}`;
          } else if (path.includes('/matchups/')) {
            const parts = path.split('/');
            const leagueId = parts[1];
            const week = parts[3];
            url = `/api/sleeper/matchups/${leagueId}/${week}`;
          } else {
            // Just league endpoint
            const leagueId = path.split('/')[1];
            url = `/api/sleeper/league/${leagueId}`;
          }
        } else {
          // Direct API call for non-proxied endpoints
          url = `${BASE_URL}${endpoint}`;
        }
      } else {
        // Local development - use Sleeper API directly
        url = `${BASE_URL}${endpoint}`;
      }
      
      const response = await axios.get(url);
      
      return {
        success: true,
        data: response.data,
        platform: 'sleeper'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        platform: 'sleeper'
      };
    }
  }

  private async getPlayers(): Promise<{ [key: string]: any }> {
    // Return cached players if available
    if (Object.keys(this.playerCache).length > 0) {
      return this.playerCache;
    }

    try {
      // Use our proxy endpoint for player data
      const proxyUrl = typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? 'http://localhost:3000/api/sleeper/players'
        : '/api/sleeper/players';
        
      const response = await axios.get(proxyUrl);
      this.playerCache = response.data;
      return this.playerCache;
    } catch (error) {
      return {};
    }
  }

  async getLeague(leagueId: string): Promise<ApiResponse<League>> {
    const response = await this.makeRequest<SleeperLeague>(`/league/${leagueId}`);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load league data',
        platform: 'sleeper'
      };
    }

    const league: League = {
      id: response.data.league_id,
      name: response.data.name,
      platform: 'sleeper',
      totalTeams: response.data.total_teams,
      currentWeek: response.data.settings.leg,
      season: parseInt(response.data.season),
      settings: {
        scoringType: 'standard',
        playoffWeeks: response.data.settings.playoff_teams,
        regularSeasonWeeks: response.data.settings.last_scored_leg
      }
    };

    return {
      success: true,
      data: league,
      platform: 'sleeper'
    };
  }

  async getLeagueUsers(leagueId: string): Promise<ApiResponse<User[]>> {
    const response = await this.makeRequest<SleeperUser[]>(`/league/${leagueId}/users`);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load users data',
        platform: 'sleeper'
      };
    }

    const users: User[] = response.data.map(user => ({
      userId: user.user_id,
      displayName: user.display_name || user.username,
      avatar: user.avatar
    }));

    return {
      success: true,
      data: users,
      platform: 'sleeper'
    };
  }

  async getLeagueRosters(leagueId: string): Promise<ApiResponse<Roster[]>> {
    const [rostersResponse, usersResponse, players] = await Promise.all([
      this.makeRequest<SleeperRoster[]>(`/league/${leagueId}/rosters`),
      this.getLeagueUsers(leagueId),
      this.getPlayers()
    ]);

    if (!rostersResponse.success || !rostersResponse.data) {
      return {
        success: false,
        error: rostersResponse.error || 'Failed to load roster data',
        platform: 'sleeper'
      };
    }

    // Create user lookup for team names
    const userLookup = new Map<string, string>();
    if (usersResponse.success && usersResponse.data) {
      usersResponse.data.forEach(user => {
        userLookup.set(user.userId, user.displayName);
      });
    }

    const rosters: Roster[] = rostersResponse.data.map(roster => {
      // Map player IDs to player objects with names
      const playerObjects: Player[] = (roster.players || []).map(playerId => {
        const player = players[playerId];
        
        // Better name construction
        let playerName = playerId;
        if (player) {
          if (player.full_name) {
            playerName = player.full_name;
          } else if (player.first_name && player.last_name) {
            playerName = `${player.first_name} ${player.last_name}`;
          } else if (player.first_name) {
            playerName = player.first_name;
          } else if (player.last_name) {
            playerName = player.last_name;
          }
        }
        
        return {
          id: playerId,
          name: playerName,
          position: player?.position || 'UNK',
          team: player?.team || 'FA'
        };
      });

      // Log roster data for debugging
      console.log(`Sleeper roster for ${userLookup.get(roster.owner_id)}: ${playerObjects.length} players`);
      if (playerObjects.length > 0) {
        console.log('Sample player:', playerObjects[0]);
      }

      // Separate starters and bench as player ID arrays (strings)
      const starterIds = roster.starters || [];
      const benchIds = (roster.players || []).filter(playerId => 
        !starterIds.includes(playerId)
      );

      return {
        userId: roster.owner_id,
        teamName: userLookup.get(roster.owner_id) || `Team ${roster.owner_id}`,
        players: playerObjects,
        starters: starterIds,
        bench: benchIds,
        totalPoints: roster.settings?.fpts || 0
      };
    });

    return {
      success: true,
      data: rosters,
      platform: 'sleeper'
    };
  }

  async getMatchups(leagueId: string, week: number): Promise<ApiResponse<Matchup[]>> {
    const [matchupsResponse, usersResponse, rostersResponse] = await Promise.all([
      this.makeRequest<SleeperMatchup[]>(`/league/${leagueId}/matchups/${week}`),
      this.getLeagueUsers(leagueId),
      this.makeRequest<SleeperRoster[]>(`/league/${leagueId}/rosters`)
    ]);

    if (!matchupsResponse.success || !matchupsResponse.data) {
      return {
        success: false,
        error: matchupsResponse.error || 'Failed to load matchups data',
        platform: 'sleeper'
      };
    }

    // Create user lookup
    const userLookup = new Map<string, string>();
    if (usersResponse.success && usersResponse.data) {
      usersResponse.data.forEach(user => {
        userLookup.set(user.userId, user.displayName);
      });
    }

    // Create roster_id to owner_id mapping
    const rosterToOwnerLookup = new Map<number, string>();
    if (rostersResponse.success && rostersResponse.data) {
      rostersResponse.data.forEach(roster => {
        rosterToOwnerLookup.set(roster.roster_id, roster.owner_id);
      });
    }

    // Group matchups by matchup_id
    const matchupGroups = new Map<number, SleeperMatchup[]>();
    matchupsResponse.data.forEach(matchup => {
      if (!matchupGroups.has(matchup.matchup_id)) {
        matchupGroups.set(matchup.matchup_id, []);
      }
      matchupGroups.get(matchup.matchup_id)!.push(matchup);
    });

    const matchups: Matchup[] = [];
    matchupGroups.forEach(matchupPair => {
      if (matchupPair.length === 2) {
        const [team1Data, team2Data] = matchupPair;
        
        // Get owner IDs from roster IDs, then get team names
        const team1OwnerId = rosterToOwnerLookup.get(team1Data.roster_id);
        const team2OwnerId = rosterToOwnerLookup.get(team2Data.roster_id);
        const team1Name = team1OwnerId ? userLookup.get(team1OwnerId) : undefined;
        const team2Name = team2OwnerId ? userLookup.get(team2OwnerId) : undefined;
        
        matchups.push({
          week,
          team1: {
            userId: team1Data.roster_id.toString(),
            teamName: team1Name,
            points: team1Data.points,
            roster: team1Data.starters.map(playerId => ({
              id: playerId,
              name: playerId, // Will need player name lookup
              position: 'Unknown',
              team: 'Unknown'
            }))
          },
          team2: {
            userId: team2Data.roster_id.toString(),
            teamName: team2Name,
            points: team2Data.points,
            roster: team2Data.starters.map(playerId => ({
              id: playerId,
              name: playerId, // Will need player name lookup
              position: 'Unknown',
              team: 'Unknown'
            }))
          }
        });
      }
    });

    return {
      success: true,
      data: matchups,
      platform: 'sleeper'
    };
  }

  // Utility method to get all league data at once
  async getLeagueData(leagueId: string, week?: number) {
    const currentWeek = week || 1; // Default to week 1 if not provided
    
    const [league, rosters, matchups] = await Promise.all([
      this.getLeague(leagueId),
      this.getLeagueRosters(leagueId),
      this.getMatchups(leagueId, currentWeek)
    ]);

    return {
      league,
      rosters,
      matchups
    };
  }
}

export const sleeperApi = new SleeperAPI();